import { Box, Flex, Heading, Section, Text } from "@radix-ui/themes";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import React from "react";
import { Link } from "react-router-dom";

export const PrivacyPolicy: React.FunctionComponent = () => {
  return (
    <Flex>
      <Box>
        <Section size="1">
          <Heading as="h2" size="4" mb="1">
            1. Data manager
          </Heading>
          <Heading as="h3" mt="3" size="3" mb="1">
            1.1. Purpose
          </Heading>
          <Text as="p" mb="2">
            As we treat personal information, we have adopted the following
            Privacy Policy, which describes how we handle information about you.
          </Text>
          <Text as="p" mb="2">
            In order to protect your personal data as much as possible, we
            constantly assess the risk of our data processing negatively
            affecting your fundamental rights. We are particularly aware of the
            risk of discrimination, identity theft, financial loss, loss of
            reputation and confidentiality of data.
          </Text>
          <Heading as="h3" mt="3" size="3">
            1.2. Contact information
          </Heading>
          <Text as="p" mb="2">
            {brand.event.isEvent ? "iPlay ApS" : "Danmarks Bedste Gamer ApS"} as
            data manager ensures that your personal information is processed in
            accordance with the law.
          </Text>
          <Text weight="bold" as="p">
            {brand.event.isEvent ? "iPlay ApS" : "Danmarks Bedste Gamer ApS"}
          </Text>
          <Text as="div">Hersegade 22, </Text>
          <Text as="div">DK-4000 Roskilde </Text>
          <Text as="div">
            VAT no: {brand.event.isEvent ? "DK28889410" : "DK31944317"}{" "}
          </Text>
          <Text as="div">Telephone: (+45) 53651565 </Text>
          <Text as="div">
            Email:{" "}
            {brand.event.isEvent
              ? "info@iplay.dk"
              : "play@danmarksbedstegamer.dk"}{" "}
          </Text>
          <Text as="div">
            Site:{" "}
            <Link target="_blank" to={`https://${brand.site}`}>
              https://{brand.site}
            </Link>
          </Text>
          <Heading as="h3" mt="3" size="3">
            1.3. We ensure fair and transparent data processing
          </Heading>
          <Text as="p" mb="2">
            If we ask you to submit your personal information to us, we will
            inform you about what information we process and to what purpose we
            process them. You can get information about this at the time of
            submitting personal data.
          </Text>
          <Text as="p" mb="2">
            In the case of us collecting data from a third party (e.g.,
            partners, suppliers, or authorities), we will notify you within 14
            days of receiving your personal information. We will also announce
            the purpose of the data collection and the legal basis that allows
            us to collect your personal information.
          </Text>
        </Section>
        <Section size="1">
          <Heading as="h2" size="4" mb="1">
            2. What data is collected
          </Heading>
          <Text as="p" mb="2">
            We collect information about you to improve our service, to ensure
            the quality of our products and services, and to enable us to
            communicate with you. The data collected includes:
          </Text>
          <Heading as="h3" mt="3" size="3">
            2.1. Automatically collected data
          </Heading>
          <Text as="p" mb="2">
            Our digital solutions are based on a variety of technologies to
            ensure user-friendliness and security. These technologies are able
            to automatically collect data and provide the best possible solution
            either directly by us or by a third party on our behalf. An example
            of this is the analysis of websites clicking data and cookies.
          </Text>
          <Text as="p" mb="2">
            All visits to any digital solutions lead to information from your
            browser being sent to a server. For the optimization of our digital
            solutions, we analyze these data. Data is collected through third
            parties, on our behalf. From your computer, data can be collected
            for system management and internal marketing analysis. These data
            are statistical information on user behavior in digital solutions.
            Examples of collected and analyzed data:
          </Text>
          <ul>
            <li>Date and time of visit.</li>
            <li>Visited pages in our digital solution.</li>
            <li>Visitors IP address.</li>
            <li>Geographical location.</li>
            <li>
              Information about the browser and computer you are using (type,
              version, operating system, etc.)
            </li>
            <li>
              Referral location URL (page from which the visitor reached our
              website)
            </li>
          </ul>
          <Text as="p" mb="2">
            We use Plausible as a web analytics solution in our digital
            solutions. In this regard, the web analytics data collected by our
            digital solutions is sent to the service offered by Plausible.
            Plausible acts as an authorized data processor for data that we are
            data administrators. We are responsible for providing these data
            only by agreement and complying with legal requirements.
          </Text>
          <Text as="p" mb="2">
            Read more about the privacy policies we use:
          </Text>
          <ul>
            <li>
              <Link to="https://plausible.io/dpa" target="_blank">
                Plausible Analytics Data Processing Agreement
              </Link>
            </li>
          </ul>
          <Heading as="h3" mt="3" size="3">
            2.2. Your submitted data
          </Heading>
          <Text as="p" mb="2">
            In addition to automatically collecting data, we also process data
            that our users are actively providing us with.
          </Text>
          <Text as="p" mb="2">
            The data that users are actively presenting are, for example:
          </Text>
          <ul>
            <li>Information sent via social media</li>
            <li>Information sent via email</li>
            <li>Information we receive from you in a contact form</li>
            <li>Information shared at polls, events and shows</li>
            <li>etc.</li>
          </ul>
        </Section>
        <Section size="1">
          <Heading as="h2" size="4" mb="1">
            3. Use and storage of collected data
          </Heading>
          <Heading as="h3" mt="3" size="3">
            3.1. The purpose of the collection
          </Heading>
          <Text as="p" mb="2">
            Below are examples of using the information we collect:
          </Text>
          <ul>
            <li>Sales follow-up.</li>
            <li>Communication regarding your booking or other inquiries.</li>
            <li>Making communication with us easy and safe for you.</li>
            <li>Continuous optimization of digital solutions.</li>
            <li>
              In some cases participation in customer surveys, competitions,
              give-aways and other digital solutions.
            </li>
            <li>
              Ability to target sales offers and campaigns based on user
              behavior history and demographic information with third-party
              solutions and data.
            </li>
          </ul>
          <Heading as="h3" mt="3" size="3">
            3.2. Storage
          </Heading>
          <Text as="p" mb="2">
            We will keep records of you if we have a legitimate and purposeful
            legal basis for this, including in order for us to be able to serve
            you as good as possible.
          </Text>
          <Heading as="h3" mt="3" size="3">
            3.3. Disclosure of information
          </Heading>
          <Text as="p" mb="2">
            We will disclose customer information to third parties only in
            accordance with this Privacy Policy.
          </Text>
          <Text as="p" mb="2">
            We may disclose your information if we have to do so in order to
            comply with a legal obligation. Transmission may also take place at
            the discretion of the court or other authority or to protect
            trademarks, rights or property. This includes the exchange of
            information with other companies and organizations for anti-fraud
            protection.
          </Text>
          <Text as="p" mb="2">
            We use service providers and data processors who work on our behalf.
            Services may be, for example: web hosting and system maintenance,
            analyzes, payment solutions, email services, etc. These
            collaborators will be able to access the necessary data for the
            provision of services. The partners are contractually bound to treat
            all information with strict confidentiality and therefore the data
            may not be used for anything other than what is covered by the
            contractual obligation. We check that our partners are fulfilling
            their responsibilities. When we disclose your information to a
            service provider or reporting agent outside the European Union, we
            will ensure that we comply with the regulatory requirements for such
            transfers. We will never collect personal information unless you
            have given us permission when ordering, purchasing, or participating
            in surveys, and so on.
          </Text>
        </Section>
        <Section size="1">
          <Heading as="h2" size="4" mb="1">
            4. Cookies
          </Heading>
          <Heading as="h3" mt="3" size="3">
            4.1. What are cookies?
          </Heading>
          <Text as="p" mb="2">
            Cookies are small text files that contain letters and numbers that
            are inserted into your computer or other device. Cookies will be
            entered when you visit a website that uses cookies and their purpose
            is to track for example what pages you have visited. Cookies will
            help you continue where you left off and they will remember your
            preferences (e.g., language settings). When we insert cookies, you
            will be informed about the use and purpose of cookies. Before the
            cookies are inserted on your device, we ask for your consent.
            However, the cookies required to ensure the functionality and
            settings can be used without your permission.
          </Text>
          <Heading as="h3" mt="3" size="3">
            4.2. Cookies and their goals
          </Heading>
          <Text as="p" mb="2">
            We use the following cookie types:
          </Text>
          <ul>
            <li>
              Personalized cookies: Use information to sort the content in order
              to display relevant content for you.
            </li>
            <li>
              Functional cookies: Necessary for the operation of the website.
              <ul>
                <li>
                  CookieConsent. Remembers your choice when clicking on the
                  cookie consent banner. Stored locally for 30 days.{" "}
                </li>
              </ul>
            </li>
          </ul>
          <Heading as="h3" mt="3" size="3">
            4.3. Delete or disable cookies in your browser
          </Heading>
          <Text as="p" mb="2">
            By changing the browser settings, you can always delete or disable
            cookies on your computer. The location of your browser settings
            depends on which browser you are using. Remember, if you do so,
            there are many features and services on the Internet that you can no
            longer use.
          </Text>
          <Text as="p" mb="2">
            All browsers allow you to delete your cookies together or
            separately. How to do this depends on which browser you are using.
            Do not forget that if you use more than one browser, delete the
            cookies from all of them.
          </Text>
          <Text as="p" mb="2">
            <Text weight="bold">Browser on the computer</Text>
          </Text>
          <ul>
            <li>
              <a
                target="_blank"
                href="https://support.microsoft.com/en-gb/windows/manage-cookies-in-microsoft-edge-view-allow-block-delete-and-use-168dab11-0753-043d-7c16-ede5947fc64d#ie=ie-10"
              >
                How to delete cookies in Microsoft Internet Explorer
              </a>
            </li>
            <li>
              <a
                target="_blank"
                href="https://support.microsoft.com/en-us/microsoft-edge/view-and-delete-browser-history-in-microsoft-edge-00cf7943-a9e1-975a-a33d-ac10ce454ca4"
              >
                How to delete cookies in Microsoft Edge
              </a>
            </li>
            <li>
              <a
                target="_blank"
                href="https://support.mozilla.org/et/kb/clear-cookies-and-site-data-firefox?redirectslug=delete-cookies-remove-info-websites-stored&redirectlocale=en-US"
              >
                How to delete cookies in Mozilla Firefox
              </a>
            </li>
            <li>
              <a
                target="_blank"
                href="https://support.google.com/chrome/answer/95647?co=GENIE.Platform%3DDesktop&hl=en"
              >
                How to delete cookies in Google Chrome
              </a>
            </li>
            <li>
              <a
                target="_blank"
                href="https://help.opera.com/en/latest/web-preferences/"
              >
                How to delete cookies in Opera
              </a>
            </li>
            <li>
              <a
                target="_blank"
                href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac"
              >
                How to delete cookies in Apple Safari (MacOS)
              </a>
            </li>
          </ul>
          <Text as="p" mb="2">
            <Text weight="bold">Browser on the mobile</Text>
          </Text>
          <ul>
            <li>
              <a target="_blank" href="https://support.apple.com/en-us/105082">
                How to delete cookies in Apple Safari (iOS)
              </a>
            </li>
            <li>
              <a
                target="_blank"
                href="https://support.google.com/chrome/answer/95647?hl=en&co=GENIE.Platform%3DAndroid&oco=0"
              >
                How to delete cookies in Android phone
              </a>
            </li>
          </ul>
        </Section>
        <Section size="1">
          <Heading as="h2" size="4" mb="1">
            5. Security
          </Heading>
          <Text as="p" mb="2">
            We will protect your personal information and comply with our
            internal data protection rules.
          </Text>
          <Text as="p" mb="2">
            We have taken into use an Internal Data Protection Policy that
            contains guidelines and measures on how to protect your personal
            information in the event of destruction, loss, unauthorized
            disclosure and unauthorized access.
          </Text>
          <Text as="p" mb="2">
            We have created rules for regulating the control of our
            employees&rsquo; access to personal data that is sensitive and to
            data that shows information about personal interests and habits. We
            control their actual access by activity logs and monitoring.
          </Text>
          <Text as="p" mb="2">
            To avoid data loss, we will continuously back up our databases. We
            also protect the confidentiality and authenticity of your data by
            encryption. In the event of a security breach leading to a high
            level of discrimination risk, identity theft, financial loss,
            reputation or other significant inconvenience, we will notify you as
            soon as possible.
          </Text>
        </Section>
        <Section size="1">
          <Heading as="h2" size="4" mb="1">
            6. Your rights
          </Heading>
          <Heading as="h3" mt="3" size="3">
            6.1. General
          </Heading>
          <Text as="p" mb="2">
            If you wish to access, modify or delete your data, or file a
            complaint against our data processing, we will investigate if / how
            this is possible and we will respond to you as soon as possible and
            no later than one month after your request has been sent.
          </Text>
          <Heading as="h3" mt="3" size="3">
            6.2. You have the right to access your personal data
          </Heading>
          <Text as="p" mb="2">
            You have the right at any time to control what data we store about
            you, where it came from and for what purpose we use it. You can also
            check how long we will store your personal information and who will
            receive it in case we need to disclose your information.
          </Text>
          <Text as="p" mb="2">
            If you wish for it, we can give you access to your data that we
            process. However, access may be restricted to protect the privacy of
            others, business secrets and intellectual property rights.
          </Text>
          <Text as="p" mb="2">
            Contact us if you want to exercise your rights. Our contact details
            are at the top. 1.2.
          </Text>
          <Heading as="h3" mt="3" size="3">
            6.3. You have the right to correct or erase inaccurate personal
            information.
          </Heading>
          <Text as="p" mb="2">
            If you believe that the personal data handled is inaccurate, you are
            entitled to correct it. In this case, you need to contact us to show
            which data is inaccurate and how we can fix it.
          </Text>
          <Text as="p" mb="2">
            In some cases, we are required to delete your personal information.
            This applies, for example, if you want to reject your consent. If
            you think your data is no longer necessary for your goals, you can
            request that it would be deleted. You can also contact us if you
            believe that your personal data will be processed against any laws
            or other legal obligations.
          </Text>
          <Text as="p" mb="2">
            When you call for a correction or deletion of your personal
            information, we will investigate if the conditions are met and then
            apply changes in this case or delete the data as soon as possible.
          </Text>
          <Heading as="h3" mt="3" size="3">
            6.4. You have the right to object to the processing of your personal
            data.
          </Heading>
          <Text as="p" mb="2">
            You have the right to object to the processing of your personal
            data. You may also oppose to the disclosure of data for marketing
            purposes. You can use the contact information at the top to send the
            complaint. If your opposition is justified, we will stop processing
            your personal data.
          </Text>
          <Heading as="h3" mt="3" size="3">
            6.5. In certain cases, you may be entitled to get your information
            delivered.
          </Heading>
          <Text as="p" mb="2">
            You have the right to receive your personal data that you have made
            available to us and collected from other parties with your consent.
            If we process information about you as part of the contract, you can
            also request your information. You also have the right to transfer
            these personal data to another service provider.
          </Text>
          <Text as="p" mb="2">
            If you want to exercise your right to transfer data, you will
            receive your personal information from us in a public format.
          </Text>
        </Section>
        <Section size="1">
          <Heading as="h2" size="4" mb="1">
            7. Version
          </Heading>
          <Text as="p" mb="2">
            This Privacy Policy was last updated on February 10th, 2025.
          </Text>
        </Section>
      </Box>
    </Flex>
  );
};
